import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Icon,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";

import { clientService, IListagemCliente } from "../../../shared/services/api/client/clientService";
import { Loading, ToolbarGridClientes } from "../../../shared/components";
import { LoadingComp } from "../../../shared/components/loading/Loading";
import { LayoutBaseDePagina } from "../../../shared/layouts";
import { useDebounce } from "../../../shared/hooks";
import { Environment } from "../../../shared/environment";
import moment from "moment";
import 'moment/locale/pt-br';
import { BasicModal } from "../../../shared/components/VModal/VModal";
import { useProfile } from "../../../context/ProfileContext";
import { checkByProfileGuard, ProfileGuard } from '../../../shared/guards/ProfileGuard';
import { PermissionActionsEnum, PermissionRolesEnum } from '../../../shared/enums/profiles';
import { useToast } from '../../../shared/hooks';
import { ToastContent } from '../../../shared/components';
import { returnMessageError } from "../../../shared/utils/returnMessageError";
import { ToolbarListagemClientes } from "./ToolbarListagemClientes";


function colorBackGround(status: string) {
  if (status === 'pending') { return '#f8e8d2' }
  if (status === 'active') { return '#d9f8e5' }
  if (status === 'inactive') { return '#fce4e4' }
  return '#959595';
}
function colorIcon(status: string) {
  if (status === 'pending') { return '#f5ba5c' }
  if (status === 'active') { return '#34D399' }
  if (status === 'inactive') { return '#F87171' }
  return '#202020';
}
function colorText(status: string) {
  if (status === 'pending') { return '#e48509' }
  if (status === 'active') { return '#0f8a0d' }
  if (status === 'inactive') { return '#e40909' }
  return '#202020';
}

function translate(traduzir: String) {
  if (traduzir === 'pending') { return 'Pendente' }
  if (traduzir === 'active') { return 'Ativo' }
  if (traduzir === 'inactive') { return 'Inativo' }
  if (traduzir === 'blocked') { return 'Bloqueado' }
  if (traduzir === 'contract_pending') { return 'Contrato' }
  return traduzir;
};

function getMonthDefaultRange() {
  const today = new Date();
  const ini = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split("T")[0];
  const fim = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split("T")[0];
  return { ini, fim };
}

const INDEFINITE_START_DATE = "2000-01-01";

function getIndefiniteDefaultRange() {
	const today = new Date();
	const ini = INDEFINITE_START_DATE;
	const fim = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split("T")[0];
	return { ini, fim };
}

function normalizeSelectValue(value: string) {
  return value?.trim()?.length ? value : "Todos";
}


export const ListagemClientesPresentation: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { debounce } = useDebounce();
  const navigate = useNavigate();
  const [rows, setRows] = useState<IListagemCliente[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const toastHandler = useToast();
  const permissionUpdate = checkByProfileGuard(PermissionRolesEnum.ClientsOptions, PermissionActionsEnum.Update)
  const busca = useMemo(() => {
    return searchParams.get('busca') || '';
  }, [searchParams]);

  const pagina = useMemo(() => {
    return Number(searchParams.get('pagina') || '1');
  }, [searchParams]);

  const rowsPerPage = useMemo(() => {
    return Number(searchParams.get('rowsPerPage') || '10');
  }, [searchParams]);

  const tipocliente = useMemo(() => {
    return (searchParams.get('tipo') || 'Todos');
  }, [searchParams]);

  const statuscliente = useMemo(() => {
    return (searchParams.get('status') || '');
  }, [searchParams]);

  const ufcliente = useMemo(() => {
    return (searchParams.get('uf') || '');
  }, [searchParams]);

  const origemcliente = useMemo(() => {
    return (searchParams.get('origem') || '');
  }, [searchParams]);

  const clienteFilter = useMemo(() => {
    return (searchParams.get('clienteFilter') || '');
  }, [searchParams]);

  const dataIni = useMemo(() => {
    //return (searchParams.get('dataini') || moment().startOf('month').format("YYYY-MM-DD"));
    return (searchParams.get('dataini') || '');//moment().startOf('month').subtract(1,'months').format("YYYY-MM-DD"));
  }, [searchParams]);

  const dataFim = useMemo(() => {
    return (searchParams.get('datafim') || ''); //moment().endOf('month').format("YYYY-MM-DD"));
  }, [searchParams]);

  const managerId = useMemo(() => {
    return (searchParams.get('managerId') || '');
  }, [searchParams]);

  const [openModal, setOpenModal] = useState(false);

  const hasViewPermission = useMemo(() => {
    return checkByProfileGuard(PermissionRolesEnum.ClientsOptions, PermissionActionsEnum.View)
  }, [])

  const { registerClient, permissionLevel, isSuperAdmin } = useProfile();

  const canEditByMenu = registerClient?.update === true;
  const canOpenEditScreen = canEditByMenu && ((permissionLevel === 1) || permissionUpdate);

  const canWalletTransfer = useMemo(() => {
    return checkByProfileGuard(PermissionRolesEnum.ClientsOptions, PermissionActionsEnum.WalletTransfer);
  }, []);

  const hasWalletTransferFeature = Boolean(registerClient.walletTransfer);

  const [filterStatus, setFilterStatus] = useState(() => normalizeSelectValue(statuscliente));
  const [filterOrigem, setFilterOrigem] = useState(() => normalizeSelectValue(origemcliente));
  const [filterUF, setFilterUF] = useState(() => normalizeSelectValue(ufcliente));
  const [filterManagerId, setFilterManagerId] = useState(() => managerId);
  const [filterDataIni, setFilterDataIni] = useState(() => dataIni || getIndefiniteDefaultRange().ini);
  const [filterDataFim, setFilterDataFim] = useState(() => dataFim || getIndefiniteDefaultRange().fim);
  const [filterBusca, setFilterBusca] = useState(() => busca);

  useEffect(() => {
    setFilterStatus(normalizeSelectValue(statuscliente));
    setFilterOrigem(normalizeSelectValue(origemcliente));
    setFilterUF(normalizeSelectValue(ufcliente));
    setFilterManagerId(managerId);
    setFilterDataIni(dataIni || getIndefiniteDefaultRange().ini);
    setFilterDataFim(dataFim || getIndefiniteDefaultRange().fim);
    setFilterBusca(busca);
  }, [statuscliente, origemcliente, ufcliente, managerId, dataIni, dataFim, busca]);

  useEffect(() => {
    if (dataIni && dataFim) return;

    const { ini, fim } = getIndefiniteDefaultRange();

    setSearchParams({
      busca,
      pagina: String(pagina),
      tipo: tipocliente,
      status: statuscliente,
      origem: origemcliente,
      clienteFilter: clienteFilter,
      uf: ufcliente,
      managerId,
      dataini: dataIni || ini,
      datafim: dataFim || fim,
      rowsPerPage: String(rowsPerPage),
    }, { replace: true });
  }, [
    dataIni,
    dataFim,
    busca,
    pagina,
    tipocliente,
    statuscliente,
    origemcliente,
    clienteFilter,
    ufcliente,
    managerId,
    setSearchParams,
    rowsPerPage,
  ]);

  useEffect(() => {
    const currentRowsPerPage = searchParams.get('rowsPerPage');
    if (currentRowsPerPage) return;

    setSearchParams({
      busca,
      pagina: String(pagina),
      tipo: tipocliente,
      status: statuscliente,
      origem: origemcliente,
      clienteFilter: clienteFilter,
      uf: ufcliente,
      managerId,
      dataini: dataIni,
      datafim: dataFim,
      rowsPerPage: '10',
    }, { replace: true });
  }, [
    searchParams,
    setSearchParams,
    busca,
    pagina,
    tipocliente,
    statuscliente,
    origemcliente,
    clienteFilter,
    ufcliente,
    managerId,
    dataIni,
    dataFim,
  ]);

  const handleOpenModal = () => {
    setOpenModal(true)
    console.log('chamando funcao')
  };

  useEffect(() => {
    if (!dataIni || !dataFim) return;

    if (hasViewPermission) {
      setIsLoading(true);
      debounce(() => {
        clientService.getAll(pagina, busca, tipocliente, origemcliente, statuscliente, clienteFilter, dataIni, dataFim, ufcliente, managerId, rowsPerPage)
          .then((result) => {
            setIsLoading(false);
            if (result instanceof Error) {
              const titleMessage = "Alerta!!";
              const contentMessage = "Erro: " + result?.message;

              toastHandler.present({
                type: 'error',
                position: 'top-right',
                messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />
              })
            } else {
              setTotalCount(result.totalCount);
              setRows(result.data);
            }
          }).catch((err: any) => {
            setIsLoading(false);
            const { titleMessage, contentMessage } = returnMessageError(err);

            toastHandler.present({
              type: 'error',
              position: 'top-right',
              messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />
            })
          })
      });
    }
  }, [tipocliente, busca, pagina, origemcliente, statuscliente, clienteFilter, dataIni, dataFim, ufcliente, hasViewPermission, managerId, rowsPerPage]);

  const updateParams = (next: {
    busca?: string;
    pagina?: string;
    tipo?: string;
    status?: string;
    origem?: string;
    dataini?: string;
    datafim?: string;
    uf?: string;
    managerId?: string;
    clienteFilter?: string;
    rowsPerPage?: string;
  }) => {
    setSearchParams(
      {
        busca,
        pagina: String(pagina),
        tipo: tipocliente,
        status: statuscliente,
        origem: origemcliente,
        dataini: dataIni,
        datafim: dataFim,
        clienteFilter: clienteFilter,
        uf: ufcliente,
        managerId,
        rowsPerPage: String(rowsPerPage),
        ...next,
      },
      { replace: true }
    );
  };

  const clickEdit = (id: string) => {
    console.log("permi", permissionLevel);
    if (permissionLevel) {
      if (canOpenEditScreen) {
        window.open(`/clientes/detalhe/${id}`)
        //navigate(`/clientes/detalhe/${id}`)
      } else {
        window.open(`${URL}/clientes/detalheunico/${id}`)
        //navigate(`/clientes/detalheunico/${id}`)
      }
    }
  }

  const textBuscaGridHandler = (texto: string) => {
    if (texto) {
      return setSearchParams({
        busca: texto,
        pagina: '1',
        tipo: tipocliente,
        status: statuscliente,
        origem: origemcliente,
        dataini: dataIni,
        datafim: dataFim,
        clienteFilter: clienteFilter,
        uf: ufcliente,
        managerId,
        rowsPerPage: String(rowsPerPage),
      }, { replace: true }
      )
    }

    return setSearchParams({
      busca: texto,
      pagina: '1',
      tipo: tipocliente,
      status: statuscliente,
      origem: origemcliente,
      dataini: dataIni,
      datafim: dataFim,
      clienteFilter: clienteFilter,
      uf: ufcliente,
      managerId,
      rowsPerPage: String(rowsPerPage),
    }, { replace: true }
    )
  }

  const handlePesquisar = () => {
    updateParams({
      busca: filterBusca,
      pagina: String(1),
      status: filterStatus,
      origem: filterOrigem,
      dataini: filterDataIni,
      datafim: filterDataFim,
      uf: filterUF,
      managerId: filterManagerId,
    });
  };


  return (
    <LayoutBaseDePagina
      titulo={permissionLevel && permissionLevel<=10? "Clientes" : "Perfil de cadastro"}
      subtitulo={permissionLevel && permissionLevel<=10? "Listagem de clientes" : "Meus Cadastros"}
      barraDeFerramentas={
        isSuperAdmin || permissionLevel == 2 ?
          <ToolbarListagemClientes
            status={filterStatus}
            origem={filterOrigem}
            uf={filterUF}
            managerId={filterManagerId}
            dataIni={filterDataIni}
            dataFim={filterDataFim}
            textSearch={filterBusca}
            onChangeTextSearch={setFilterBusca}
            onClearTextSearch={() => {
              setFilterBusca("");
              updateParams({
                busca: "",
                pagina: String(1),
                status: filterStatus,
                origem: filterOrigem,
                dataini: filterDataIni,
                datafim: filterDataFim,
                uf: filterUF,
                managerId: filterManagerId,
              });
            }}
            showPeriodo
            showUF
            showStatus={checkByProfileGuard(PermissionRolesEnum.ClientsOptions, PermissionActionsEnum.Filter)}
            showOrigem={checkByProfileGuard(PermissionRolesEnum.ClientsOptions, PermissionActionsEnum.Filter)}
            showManager={isSuperAdmin || permissionLevel == 2}
            onChangeStatus={setFilterStatus}
            onChangeOrigem={setFilterOrigem}
            onChangeUF={setFilterUF}
            onChangeManagerId={setFilterManagerId}
            onChangeDataIni={setFilterDataIni}
            onChangeDataFim={setFilterDataFim}
            onClickPesquisar={handlePesquisar}
            showButtonNew={checkByProfileGuard(PermissionRolesEnum.ClientsOptions, PermissionActionsEnum.Add)}
            textoButtonNew="Novo"
            onClickNew={() => navigate('/clientes/detalhe/novo')}
            showContractsMenuItem={false}
            showTransferMenuItem
            transferEnabled={hasWalletTransferFeature && canWalletTransfer}
            onClickTransfer={() => navigate('/clientes/transferencia')}
          />
          : ''

      }>

      {openModal && <BasicModal
        ITitle="Modal do Fernando"
        IMessage="Minha mensagem Generics "
        isCloseModal={() => setOpenModal(false)}
        isOpenModal={openModal}
        messageType={'info'}
        onclickConfirm={() => {
          alert('Confirmar');
          setOpenModal(false)
        }}
      />}

      {/* {isLoading && !busca?.trim()?.length ? <Loading />
        : */}
      <ProfileGuard requiredRole={PermissionRolesEnum.ClientsOptions} requiredAction={PermissionActionsEnum.View}>
        <TableContainer
          component={Paper}
          variant="elevation"
          sx={{
            mt: 1,
            width: "96%",
            px: { xs: 1, sm: 3 },
            py: { xs: 2, sm: 3 },
            ml: 1,
            borderRadius: 2,
            overflow: "hidden",
          }}
          aria-label="customized table"
        >
          <Box mb={2}>
            <ToolbarGridClientes
              showInputBuscaGrid={false}
              onClickTodos={() => updateParams({ tipo: 'Todos' })}
              onClickCorretor={() => updateParams({ tipo: 'broker' })}
              onClickImobiliaria={() => updateParams({ tipo: 'estate' })}
              onClickGestorContas={() => updateParams({ tipo: 'Gestor' })}
              selectTodos={tipocliente === 'Todos'}
              selectCorretor={tipocliente === 'broker'}
              selectImobiliaria={tipocliente === 'estate'}
              selectGestorContas={tipocliente === 'Gestor'}
              showBotaoCorretor={permissionLevel === 1 ? true : false}
              showBotaoImobiliaria={permissionLevel === 1 ? true : false}
              showBotaoGestorContas={false}
              showBotaoTodos={permissionLevel === 1 ? true : false}
            />
          </Box>

          <Table size="small" stickyHeader aria-label="a dense table" >
            <TableHead>
              <TableRow sx={{ backgroundColor: "grey.100" }}>
                <TableCell>
                  <Typography variant="button">Nome/Razão Social</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="button">Tipo</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="button">Endereço</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="button">Status</Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="button">Cadastro</Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="button">Origem</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="button">Ações</Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && !isLoading && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    Nenhum registro
                  </TableCell>
                </TableRow>
              )}

              {!isLoading && (rows.map(row => (
                <TableRow key={row.id} hover >
                  <TableCell sx={{ fontWeight: "normal" }}>
                    {row.name}
                    <Typography variant="subtitle2" color={"#959595"} fontWeight={"normal"} >
                      {row.trade_name}
                    </Typography>
                    <Typography variant="subtitle2" color={"#959595"} fontWeight={"normal"} >
                      {row.document.length > 11 ?
                        row.document.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/g, "\$1.\$2.\$3\/\$4\-\$5")
                        :
                        row.document.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/g, "\$1.\$2.\$3\-\$4")
                      }
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ fontWeight: "normal" }} >
                    <Typography variant="subtitle2" fontWeight={"normal"} >
                      {translate(row.clientType.description)}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ color: "#959595", fontWeight: "normal" }} >
                    <Typography variant="subtitle2" color={"#959595"} fontWeight={"normal"} >
                      {row.address_street != '' && row.address_street != null && row.address_street != undefined ?
                        `${row.address_complement != '' && row.address_complement != null && row.address_complement != undefined ?
                          `${row.address_street} , ${row.address_number} - ${row.address_complement} - ${row.address_district}`
                          :
                          `${row.address_street} , ${row.address_number} - ${row.address_district}`
                        }` :
                        'Endereço não cadastrado'
                      }
                    </Typography>
                    <Typography variant="subtitle2" color={"#959595"} fontWeight={"normal"} >
                      {row.address_city != '' && row.address_city != null && row.address_city != undefined ?
                        `${row.address_city} - ${row.address_state}`
                        :
                        ''
                      }
                    </Typography>
                  </TableCell>

                  < TableCell sx={{ textAlign: "start" }}>
                    <IconButton size="small" sx={{ background: colorBackGround(row.status), borderRadius: "18px", paddingX: "15px", paddingY: "1px", marginBottom: "5px" }}>
                      <Icon sx={{ color: colorIcon(row.status), fontWeight: "normal" }}> noise_control_off_sharp_icon</Icon>
                      <Typography variant="subtitle2" marginLeft={1} sx={{ color: colorText(row.status), fontWeight: "normal" }}>
                        {translate(row.status)}
                      </Typography>
                    </IconButton>
                  </TableCell>

                  <TableCell>
                    <Typography variant="subtitle2" color={"#959595"} sx={{ fontWeight: "normal", textAlign: "center" }}>
                      {moment(row.createdAt).format("L")}
                    </Typography>
                    <Typography variant="subtitle2" color={"#959595"} sx={{ fontWeight: "normal", textAlign: "center" }}>
                      {moment(row.createdAt).format("hh:mm:ss")}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ textAlign: "center" }}>
                    {(() => {
                      const createdFrom = String(row.created_from || '').toLowerCase();
                      const isWebApp = createdFrom === 'web_app' || createdFrom === 'web' || createdFrom === 'webapp';
                      const title = isWebApp ? 'Web Aplicação' : 'Integração';
                      const src = isWebApp ? '/logogar.png' : '/4seg.png';
                      const width = isWebApp ? 30 : 40;

                      return (
                        <Tooltip title={title}>
                          <Box
                            component="span"
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minWidth: 40,
                            }}
                          >
                            <Box component="img" src={src} width={width} sx={{ height: 'auto' }} />
                          </Box>
                        </Tooltip>
                      );
                    })()}
                  </TableCell>

                  <TableCell align="right">
                    <IconButton
                      disabled={!canEditByMenu}
                      size="small"
                      onClick={() => clickEdit(row.id)}
                      sx={{ borderRadius: "18px" }}
                    >
                      <Icon sx={{ color: canEditByMenu ? "#0170B3" : "action.disabled", fontWeight: "normal" }}> edit</Icon>
                      <Typography
                        variant="subtitle2"
                        marginLeft={1}
                        sx={{ color: canEditByMenu ? "#0170B3" : "text.disabled", fontWeight: "normal" }}
                      >
                        Editar
                      </Typography>
                    </IconButton>
                  </TableCell>
                </TableRow>
              )))}
            </TableBody>

            {totalCount === 0 && !isLoading && (
              <caption>{Environment.LISTAGEM_VAZIA}</caption>
            )}

          </Table>

          <LoadingComp isopen={isLoading} />

          <Box display="flex" justifyContent="flex-end" alignItems="center" mt={2} pr={2}>
            <TablePagination
              component="div"
              count={totalCount}
              page={Math.max(pagina - 1, 0)}
              rowsPerPage={rowsPerPage}
              onPageChange={(_, newPage) => updateParams({ pagina: String(newPage + 1) })}
              onRowsPerPageChange={(e) => updateParams({ pagina: '1', rowsPerPage: e.target.value })}
              rowsPerPageOptions={[10, 25, 50]}
              labelRowsPerPage="Linhas por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count} registros`}
            />
          </Box>
        </TableContainer>
      </ProfileGuard>
      {/* } */}
    </LayoutBaseDePagina >
  );
};
